import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  ContractDataFieldsDto,
  ContractDto,
  CustomContractDataFieldDto,
} from "../../../autogen/bff-api";
import { EditContractCommandType, editContractThunk } from "../thunks/contract/edit-contract-thunk";
import { loadContractThunk } from "../thunks/contract/load-contract-thunk";
import { isOlderRevision, isOlderRevisionOfSameEntity } from "./staleResponseGuard";

export type ContractState = {
  id: string;
  contract: Omit<ContractDto, "dataFields"> & {
    dataFields: ContractDataFieldsDto;
  };
  lastChanged: string;
  errors: EditContractCommandType[];
};

interface State {
  state?: ContractState | null;
  customFields?: ContractDataFieldsDto["customFields"];
  requestedContractId?: string | null;
}

const initialState: State = {
  state: null,
};

export const slice = createSlice<
  State,
  {
    setCustomFields: (state: State, action: PayloadAction<NonNullable<ContractDataFieldsDto["customFields"]>>) => void;
    setCustomField: (state: State, action: PayloadAction<{ sectionId: string; field: CustomContractDataFieldDto }>) => void;
  },
  "contract"
>({
  name: "contract",
  initialState,
  reducers: {
    setCustomFields: (state, action) => {
      state.customFields = action.payload;
    },
    setCustomField: (state, action) => {
      const customFields = state.customFields;
      if (!customFields) throw Error("Custom fields not defined");
      const fieldIndex = customFields[action.payload.sectionId].fields.findIndex(
        (f) => f.id === action.payload.field.id
      );
      if (fieldIndex === -1) throw Error("Field not found - cannot update");
      customFields[action.payload.sectionId].fields[fieldIndex] = action.payload.field;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadContractThunk.pending, (state, action) => {
      state.requestedContractId = action.meta.arg.contractId;
    });
    builder.addCase(loadContractThunk.fulfilled, (state, action) => {
      if (action.meta.arg.contractId !== state.requestedContractId) {
        return;
      }
      const payload = action.payload;
      if (state.state && payload && isOlderRevisionOfSameEntity(payload, state.state)) {
        return;
      }
      state.state = payload;
      state.customFields = payload?.contract.dataFields.customFields;
    });
    builder.addCase(editContractThunk.fulfilled, (state, action) => {
      const payload = action.payload;
      if (
        payload &&
        (!state.state || payload.id !== state.state.id || isOlderRevision(payload, state.state))
      ) {
        return;
      }
      state.state = payload;
    });
  },
});

export const { setCustomField, setCustomFields } = slice.actions;
export default slice.reducer;

export const containsError = (state: ContractState, type: EditContractCommandType): boolean => {
  return state.errors.indexOf(type) !== -1;
};
